<template>
  <main class="container-1 terrain-review-queue">
    <div v-if="!hideChrome" class="container-header">
      <Breadcrumb :links="routesBread" />
      <TabNav v-if="!campaignId" :links="routesNav" />
    </div>

    <section class="container-body terrain-review-queue__body" aria-live="polite">
      <p v-if="loading" class="terrain-review-queue__status">Cargando respuestas…</p>
      <p v-else-if="errorMessage" class="terrain-review-queue__error" role="alert">{{ errorMessage }}</p>
      <template v-else>
        <p v-if="!items.length" class="terrain-review-queue__empty">No hay respuestas pendientes de revisión.</p>
        <div v-else class="terrain-review-queue__layout">
          <div class="terrain-review-queue__list-col">
            <div class="terrain-review-queue__filters">
              <label>
                Estado
                <TerrainSelect v-model="statusFilter" :options="statusOptions" aria-label="Filtrar respuestas por estado" />
              </label>
              <label>
                Orden
                <TerrainSelect v-model="sortOrder" :options="sortOptions" aria-label="Ordenar respuestas" />
              </label>
            </div>

            <p v-if="!pendingItems.length && !resolvedItems.length" class="terrain-review-queue__empty">
              Ninguna respuesta coincide con el filtro.
            </p>

            <section v-if="pendingItems.length" class="terrain-review-queue__group">
              <div class="terrain-review-queue__group-header">
                <h4>Pendientes ({{ pendingItems.length }})</h4>
                <label class="terrain-review-queue__select-all">
                  <input type="checkbox" :checked="allPendingSelected" @change="toggleSelectAllPending">
                  Seleccionar todas
                </label>
              </div>

              <div v-if="selectedCount" class="terrain-review-queue__bulk-bar">
                <span>{{ selectedCount }} seleccionada{{ selectedCount === 1 ? '' : 's' }}</span>
                <button type="button" :disabled="bulkTransitioning" @click="confirmBulkApprove">Aprobar seleccionadas</button>
                <button type="button" :disabled="bulkTransitioning" @click="openBulkRejectModal">Rechazar seleccionadas</button>
                <button type="button" class="terrain-review-queue__bulk-clear" :disabled="bulkTransitioning" @click="clearSelection">Deseleccionar</button>
              </div>

              <ul class="terrain-review-queue__list" aria-label="Respuestas pendientes">
                <li
                  v-for="item in pendingItems"
                  :key="item.id"
                  class="terrain-review-queue__item"
                  :class="{ 'terrain-review-queue__item--active': item.id === selectedResponseId }"
                >
                  <label class="terrain-review-queue__item-checkbox" @click.stop>
                    <input
                      type="checkbox"
                      :checked="isSelected(item.id)"
                      :disabled="bulkTransitioning"
                      :aria-label="`Seleccionar respuesta de ${respondentLabel(item)}`"
                      @change="toggleSelected(item.id)"
                    >
                  </label>
                  <button type="button" class="terrain-review-queue__item-button" @click="selectResponse(item.id)">
                    <span class="terrain-review-queue__tag" :class="`terrain-review-queue__tag--${item.status}`">{{ statusLabel(item.status) }}</span>
                    <span class="terrain-review-queue__item-respondent">{{ respondentLabel(item) }}</span>
                    <span class="terrain-review-queue__item-date">{{ dateLabel(item.submittedAt) }}</span>
                  </button>
                </li>
              </ul>
            </section>

            <section v-if="resolvedItems.length" class="terrain-review-queue__group">
              <h4>Resueltas ({{ resolvedItems.length }})</h4>
              <ul class="terrain-review-queue__list" aria-label="Respuestas resueltas">
                <li
                  v-for="item in resolvedItems"
                  :key="item.id"
                  class="terrain-review-queue__item"
                  :class="{ 'terrain-review-queue__item--active': item.id === selectedResponseId }"
                >
                  <button type="button" class="terrain-review-queue__item-button" @click="selectResponse(item.id)">
                    <span class="terrain-review-queue__tag" :class="`terrain-review-queue__tag--${item.status}`">{{ statusLabel(item.status) }}</span>
                    <span class="terrain-review-queue__item-respondent">{{ respondentLabel(item) }}</span>
                    <span class="terrain-review-queue__item-date">{{ dateLabel(item.submittedAt) }}</span>
                  </button>
                </li>
              </ul>
            </section>
          </div>

          <section class="terrain-review-queue__detail" aria-label="Detalle de la respuesta">
            <p v-if="!selectedResponseId" class="terrain-review-queue__empty">Seleccione una respuesta para ver su detalle.</p>
            <p v-else-if="detailLoading" class="terrain-review-queue__status">Cargando detalle…</p>
            <p v-else-if="detailError" class="terrain-review-queue__error" role="alert">{{ detailError }}</p>
            <template v-else-if="detail">
              <header class="terrain-review-queue__detail-header">
                <div>
                  <h3>Respuesta de {{ respondentLabel(detail) }}</h3>
                  <p class="terrain-review-queue__detail-id">{{ detail.id }}</p>
                </div>
                <span class="terrain-review-queue__tag" :class="`terrain-review-queue__tag--${detail.status}`">{{ statusLabel(detail.status) }}</span>
              </header>

              <section v-if="hasLocation" class="terrain-review-queue__location">
                <h4>Ubicación</h4>
                <p v-if="canEditDetail" class="terrain-review-queue__hint">Toque el mapa para mover el marcador.</p>
                <LocationPickerMap :coordinates="locationCoordinatesForMap" :readonly="!canEditDetail" @update:coordinates="draftLocation = $event" />
              </section>

              <TerrainFormRenderer
                v-if="detail.definition"
                :definition="detail.definition"
                :answers="formAnswers"
                mode="preview"
                :readonly="!canEditDetail"
                :campaign-id="detail.campaignId"
                :template-version-id="detail.templateVersionId"
                @update:answers="draftAnswers = $event"
              />

              <section v-if="canEditDetail && hasPendingCorrection" class="terrain-review-queue__correction">
                <p v-if="correctionError" class="terrain-review-queue__error" role="alert">{{ correctionError }}</p>
                <div class="terrain-review-queue__correction-actions">
                  <button type="button" class="terrain-review-queue__correction-discard" :disabled="savingCorrection" @click="discardCorrection">Descartar</button>
                  <button type="button" class="terrain-review-queue__correction-save" :disabled="savingCorrection" @click="saveCorrection">
                    {{ savingCorrection ? "Guardando…" : "Guardar cambios" }}
                  </button>
                </div>
              </section>

              <section class="terrain-review-queue__actions">
                <label class="terrain-review-queue__comment-label">
                  Comentario
                  <span class="terrain-review-queue__hint">Se enviará como observación al rechazar; es obligatorio para rechazar o retirar</span>
                  <textarea
                    id="terrain-review-comment"
                    v-model="comment"
                    class="terrain-review-queue__comment"
                    rows="3"
                  />
                </label>

                <p v-if="actionError" class="terrain-review-queue__error" role="alert">{{ actionError }}</p>

                <div class="terrain-review-queue__buttons">
                  <button v-if="availableTargets.includes('approved')" type="button" :disabled="transitioning" @click="applyTransition('approved')">Aprobar</button>
                  <button v-if="availableTargets.includes('rejected')" type="button" :disabled="transitioning" @click="applyTransition('rejected')">Rechazar</button>
				  <button v-if="availableTargets.includes('corrected')" type="button" :disabled="transitioning" @click="applyTransition('corrected')">Marcar corregida</button>
                  <button v-if="availableTargets.includes('withdrawn')" type="button" :disabled="transitioning" @click="applyTransition('withdrawn')">Retirar</button>
                </div>
              </section>
            </template>
          </section>
        </div>
      </template>
    </section>

    <modal name="terrain-review-bulk-reject" :width="420" height="auto" :adaptive="true">
      <div class="terrain-review-queue__bulk-modal">
        <h2>Rechazar {{ selectedCount }} respuesta{{ selectedCount === 1 ? '' : 's' }}</h2>
        <p>Este comentario se aplicará a todas las respuestas seleccionadas.</p>
        <label class="terrain-review-queue__comment-label">
          Comentario
          <textarea v-model="bulkComment" class="terrain-review-queue__comment" rows="4" />
        </label>
        <p v-if="bulkModalError" class="terrain-review-queue__error" role="alert">{{ bulkModalError }}</p>
        <div class="terrain-review-queue__bulk-modal-actions">
          <button type="button" class="terrain-review-queue__bulk-modal-cancel" :disabled="bulkTransitioning" @click="closeBulkRejectModal">Cancelar</button>
          <button type="button" class="terrain-review-queue__bulk-modal-confirm" :disabled="bulkTransitioning" @click="confirmBulkReject">
            {{ bulkTransitioning ? 'Rechazando…' : 'Rechazar' }}
          </button>
        </div>
      </div>
    </modal>
  </main>
</template>

<script>
import Breadcrumb from "../../components/Breadcrumb.vue";
import TabNav from "../../components/TabNav.vue";
import TerrainSelect from "../../components/terrain/TerrainSelect.vue";
import TerrainFormRenderer from "../../components/terrain/form/TerrainFormRenderer.vue";
import LocationPickerMap from "../../components/terrain/form/LocationPickerMap.vue";
import { createTerrainApi } from "../../services/terrain/api.js";
import { terrainNavLinks } from "../../services/terrain/nav.js";
import {
  AVAILABLE_TARGETS_BY_STATUS,
  COMMENT_REQUIRED_TARGETS,
  PENDING_STATUSES,
  EDITABLE_STATUSES,
  createIdempotencyKey,
  statusLabel as sharedStatusLabel,
  transitionErrorMessage,
} from "../../services/terrain/reviewWorkflow.js";

export default {
  name: "TerrainReviewQueue",
  components: { Breadcrumb, TabNav, TerrainFormRenderer, LocationPickerMap, TerrainSelect },
  props: {
    // hideChrome: TerrainCampaignResponses.vue embebe esta vista tal cual
    // como su modo "Tarjetas" (secundario a la tabla) — sin este flag se
    // vería el breadcrumb duplicado (el wrapper ya pone el suyo). Default
    // false conserva el comportamiento de siempre en /terreno/revision y en
    // la ruta de campaña cuando nadie la embebe.
    hideChrome: { type: Boolean, default: false },
    // editMode: dueño de la bandera es TerrainCampaignResponses.vue (o, en
    // /terreno/revision, ninguna — default false conserva el comportamiento
    // de siempre ahí, sin botón de edición).
    editMode: { type: Boolean, default: false },
  },
  data() {
    return {
      statusOptions: [
        { value: "", label: "Todos" }, { value: "submitted", label: "Enviada" }, { value: "en_revision", label: "En revisión" },
        { value: "corregida", label: "Corregida" }, { value: "aprobada", label: "Aprobada" },
        { value: "rechazada", label: "Rechazada" }, { value: "retirada", label: "Retirada" },
      ],
      sortOptions: [{ value: "desc", label: "Más recientes primero" }, { value: "asc", label: "Más antiguas primero" }],
      items: [],
      loading: true,
      errorMessage: "",
      selectedResponseId: null,
      detail: null,
      detailLoading: false,
      detailError: "",
      comment: "",
      actionError: "",
      transitioning: false,
      campaignName: "",
      statusFilter: "",
      sortOrder: "desc",
      // Selección en bloque (Big Boss, 2026-08-02): solo sobre Pendientes,
      // "Resueltas" nunca es seleccionable en bloque.
      selectedIds: [],
      bulkTransitioning: false,
      bulkComment: "",
      bulkModalError: "",
      // Corrección de datos (modo edición): draftAnswers/draftLocation solo
      // se llenan cuando el usuario efectivamente toca algo (null = sin
      // cambios pendientes). draftAnswers, cuando existe, ya es el array
      // completo de answers (TerrainFormRenderer.update:answers siempre
      // emite el set entero, no un delta).
      draftAnswers: null,
      draftLocation: null,
      savingCorrection: false,
      correctionError: "",
    };
  },
  computed: {
    // Con :campaignId en la ruta esta vista queda acotada a una campaña
    // (enlazada desde el "X respuestas" de TerrainCampaigns.vue); sin él,
    // sigue siendo la pestaña global "Revisión" de siempre.
    campaignId() { return (this.$route.params || {}).campaignId || null; },
    routesBread() {
      if (this.campaignId) {
        return [
          { name: "Terreno" },
          { name: "Campañas", path: "/terreno/campanas" },
          { name: this.campaignName || "Respuestas" },
        ];
      }
      return [{ name: "Terreno" }, { name: "Revisión" }];
    },
    routesNav() { return terrainNavLinks((this.$store.getters.getPermissions || {}).terrain); },
    terrainApi() {
      return createTerrainApi(this.$http, this.$store.getters.backendurl);
    },
    availableTargets() {
      if (!this.detail) return [];
      return AVAILABLE_TARGETS_BY_STATUS[this.detail.status] || [];
    },
    hasLocation() {
      const coords = this.detail && this.detail.location && this.detail.location.coordinates;
      return Array.isArray(coords) && coords.length === 2;
    },
    // canEditDetail: modo edición activo Y el estado de la respuesta
    // seleccionada admite corrección (mismo criterio que
    // correctionEditableStatuses en el backend).
    canEditDetail() { return this.editMode && Boolean(this.detail) && EDITABLE_STATUSES.includes(this.detail.status); },
    formAnswers() { return this.draftAnswers !== null ? this.draftAnswers : ((this.detail && this.detail.answers) || []); },
    locationCoordinatesForMap() { return this.draftLocation || (this.detail && this.detail.location && this.detail.location.coordinates); },
    hasPendingCorrection() { return this.draftAnswers !== null || this.draftLocation !== null; },
    // Filtro + orden aplican sobre el lote completo antes de separar en
    // Pendientes/Resueltas, para que ambos grupos respeten el mismo criterio.
    filteredItems() {
      if (!this.statusFilter) return this.items;
      return this.items.filter((item) => item.status === this.statusFilter);
    },
    sortedItems() {
      const items = [...this.filteredItems];
      items.sort((a, b) => {
        const diff = new Date(a.submittedAt) - new Date(b.submittedAt);
        return this.sortOrder === "asc" ? diff : -diff;
      });
      return items;
    },
    pendingItems() { return this.sortedItems.filter((item) => PENDING_STATUSES.includes(item.status)); },
    resolvedItems() { return this.sortedItems.filter((item) => !PENDING_STATUSES.includes(item.status)); },
    selectedCount() { return this.selectedIds.length; },
    allPendingSelected() {
      return this.pendingItems.length > 0 && this.pendingItems.every((item) => this.selectedIds.includes(item.id));
    },
  },
  created() {
    if (this.campaignId) this.loadCampaignName();
    this.loadQueue();
  },
  watch: {
    hasPendingCorrection(next) { this.$emit("dirty-change", next); },
    editMode(next) {
      // El padre ya confirmó el descarte antes de bajar la prop a false
      // (ver dirty-change) — acá solo se limpia el estado local.
      if (!next) {
        this.draftAnswers = null;
        this.draftLocation = null;
      }
    },
  },
  methods: {
    async loadCampaignName() {
      try {
        const response = await this.terrainApi.getCampaign(this.campaignId);
        this.campaignName = (response.data && response.data.name) || "";
      } catch (error) {
        // El breadcrumb cae de vuelta a "Respuestas"; no es un error bloqueante.
      }
    },
    statusLabel(status) { return sharedStatusLabel(status); },
    dateLabel(value) {
      if (!value) return "Sin fecha";
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? "Sin fecha" : date.toLocaleString();
    },
    async loadQueue() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const params = { page: 1, size: 200 };
        if (this.campaignId) params.campaignId = this.campaignId;
        const response = await this.terrainApi.listResponses(params);
        this.items = (response.data && response.data.items) || [];
      } catch (error) {
        if (error.response && error.response.status === 401) {
          this.$router.replace({ name: "inicio", query: { returnTo: this.$route.fullPath } });
          return;
        }
        this.errorMessage = "No fue posible cargar la cola de revisión. Intente nuevamente.";
      } finally {
        this.loading = false;
      }
    },
    // selectResponse: punto de entrada del template — si hay una corrección
    // sin guardar sobre la respuesta actual, confirma antes de descartarla y
    // cambiar de selección (Etapa 4 del plan de modo edición).
    selectResponse(id) {
      if (id === this.selectedResponseId) return;
      if (this.hasPendingCorrection) {
        this.$modal.show("dialog", {
          text: "Hay cambios sin guardar en esta respuesta. ¿Cambiar de todos modos? Se perderán.",
          buttons: [
            { title: "Cancelar" },
            { title: "Cambiar sin guardar", handler: () => { this.$modal.hide("dialog"); this.discardCorrection(); this.loadResponseDetail(id); } },
          ],
        });
        return;
      }
      this.loadResponseDetail(id);
    },
    async loadResponseDetail(id) {
      this.selectedResponseId = id;
      this.detail = null;
      this.detailError = "";
      this.actionError = "";
      this.comment = "";
      this.detailLoading = true;
      try {
        const response = await this.terrainApi.getResponse(id);
        this.detail = response.data;
      } catch (error) {
        this.detailError = "No fue posible cargar el detalle de la respuesta.";
      } finally {
        this.detailLoading = false;
      }
    },
    discardCorrection() {
      this.draftAnswers = null;
      this.draftLocation = null;
      this.correctionError = "";
    },
    async saveCorrection() {
      if (!this.detail) return;
      this.savingCorrection = true;
      this.correctionError = "";
      try {
        const payload = { expectedVersion: this.detail.resourceVersion };
        if (this.draftAnswers !== null) payload.answers = this.draftAnswers;
        if (this.draftLocation !== null) payload.location = { type: "Point", coordinates: this.draftLocation, correctedAt: new Date().toISOString() };
        const result = await this.terrainApi.correctResponse(this.detail.id, payload, this.detail.resourceVersion, createIdempotencyKey());
        const data = result.data;
        this.detail = {
          ...this.detail,
          resourceVersion: data.resourceVersion,
          answers: this.draftAnswers !== null ? this.draftAnswers : this.detail.answers,
          location: this.draftLocation !== null ? { ...this.detail.location, type: "Point", coordinates: this.draftLocation } : this.detail.location,
        };
        this.items = this.items.map((item) => (item.id === this.detail.id ? { ...item, resourceVersion: data.resourceVersion } : item));
        this.draftAnswers = null;
        this.draftLocation = null;
        this.$store.dispatch("toast/show", { message: "Corrección guardada correctamente.", type: "success" });
      } catch (error) {
        this.correctionError = transitionErrorMessage(error);
      } finally {
        this.savingCorrection = false;
      }
    },
    async applyTransition(target) {
      if (!this.detail) return;
      this.actionError = "";
      if (COMMENT_REQUIRED_TARGETS.includes(target) && !this.comment.trim()) {
        this.actionError = "Ingrese un comentario antes de continuar.";
        return;
      }
      this.transitioning = true;
      try {
        const payload = { target, expectedVersion: this.detail.resourceVersion, comment: this.comment || undefined };
		if (target === "rejected") payload.observations = [{ questionId: "general", message: this.comment.trim() }];
        const response = await this.terrainApi.transitionResponse(
          this.detail.id,
          payload,
          this.detail.resourceVersion,
          createIdempotencyKey()
        );
        this.detail = { ...this.detail, status: response.data.status, resourceVersion: response.data.resourceVersion };
        this.items = this.items.map((item) => (item.id === this.detail.id ? { ...item, status: this.detail.status } : item));
        this.comment = "";
      } catch (error) {
        this.actionError = transitionErrorMessage(error);
      } finally {
        this.transitioning = false;
      }
    },
    respondentLabel(item) {
      if (!item) return "Respondente desconocido";
      if (item.recipientEmail && item.respondentUsername) return `${item.recipientEmail} (${item.respondentUsername})`;
      return item.recipientEmail || item.respondentUsername || item.recipientId || item.respondentId || "Respondente desconocido";
    },
    isSelected(id) { return this.selectedIds.includes(id); },
    toggleSelected(id) {
      this.selectedIds = this.isSelected(id)
        ? this.selectedIds.filter((selectedId) => selectedId !== id)
        : [...this.selectedIds, id];
    },
    toggleSelectAllPending() {
      this.selectedIds = this.allPendingSelected ? [] : this.pendingItems.map((item) => item.id);
    },
    clearSelection() { this.selectedIds = []; },
    confirmBulkApprove() {
      const count = this.selectedCount;
      this.$modal.show("dialog", {
        text: `¿Aprobar ${count} respuesta${count === 1 ? "" : "s"}? Las que todavía no estén "En revisión" pasan por ese paso automáticamente antes de aprobarse.`,
        buttons: [
          { title: "Cancelar" },
          { title: "Aprobar", handler: () => { this.$modal.hide("dialog"); return this.runBulkTransition("approved"); } },
        ],
      });
    },
    openBulkRejectModal() {
      this.bulkComment = "";
      this.bulkModalError = "";
      this.$modal.show("terrain-review-bulk-reject");
    },
    closeBulkRejectModal() {
      this.$modal.hide("terrain-review-bulk-reject");
    },
    confirmBulkReject() {
      if (!this.bulkComment.trim()) {
        this.bulkModalError = "Ingrese un comentario antes de continuar.";
        return undefined;
      }
      this.bulkModalError = "";
      this.closeBulkRejectModal();
      return this.runBulkTransition("rejected", this.bulkComment.trim());
    },
    // runBulkTransition aplica target (con comment, si corresponde) a cada
    // seleccionada, una por una: cada ítem conserva su propia transacción,
    // auditoría e Idempotency-Key, exactamente como si el revisor la
    // hubiera aplicado a mano — este método solo automatiza el click
    // repetido, no inventa un camino nuevo en el backend. reviewWorkflowEdges
    // (services/terrain/review_workflow.go) admite el salto directo desde
    // enviada/corregida/retirada, así que ya no hace falta pasar antes por
    // "en revisión".
    // Un fallo en un ítem no aborta el resto: se acumulan éxitos/fallos y
    // se informa al final, la cola se recarga una sola vez.
    async runBulkTransition(target, comment) {
      const ids = [...this.selectedIds];
      if (!ids.length) return;
      this.bulkTransitioning = true;
      let okCount = 0;
      const failedIds = [];
      for (const id of ids) {
        const item = this.items.find((candidate) => candidate.id === id);
        if (!item) continue;
        try {
          const resourceVersion = item.resourceVersion;
		  const payload = { target, expectedVersion: resourceVersion, comment: comment || undefined };
		  if (target === "rejected") payload.observations = [{ questionId: "general", message: comment }];
          const result = await this.terrainApi.transitionResponse(id, payload, resourceVersion, createIdempotencyKey());
          this.items = this.items.map((candidate) => (
            candidate.id === id ? { ...candidate, status: result.data.status, resourceVersion: result.data.resourceVersion } : candidate
          ));
          okCount += 1;
        } catch (error) {
          failedIds.push(id);
        }
      }
      this.bulkTransitioning = false;
      this.selectedIds = failedIds;
      if (this.selectedResponseId && ids.includes(this.selectedResponseId)) {
        this.selectResponse(this.selectedResponseId);
      }
      const verb = target === "approved" ? "aprobada" : "rechazada";
      const plural = (n) => (n === 1 ? "" : "s");
      const message = failedIds.length
        ? `${okCount} respuesta${plural(okCount)} ${verb}${plural(okCount)}, ${failedIds.length} fallaron.`
        : `${okCount} respuesta${plural(okCount)} ${verb}${plural(okCount)} correctamente.`;
      this.$store.dispatch("toast/show", { message, type: failedIds.length ? "error" : "success" });
    },
  },
};
</script>

<style scoped lang="scss">
.terrain-review-queue__body { display: grid; gap: var(--terrain-space-lg); margin: 0 auto; max-width: 1200px; width: 95%; }
.terrain-review-queue__layout { display: grid; gap: var(--terrain-space-lg); grid-template-columns: 320px 1fr; align-items: start; }
.terrain-review-queue__list-col { display: grid; gap: var(--terrain-space-md); }
.terrain-review-queue__list { display: grid; gap: var(--terrain-space-sm); list-style: none; margin: 0; padding: 0; }

.terrain-review-queue__filters {
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-md);

  label {
    display: grid;
    gap: var(--terrain-space-xs);
    color: var(--color-navy);
    font-weight: 600;
    font-size: var(--terrain-font-caption);
    flex: 1;
  }

}

.terrain-review-queue__group {
  display: grid;
  gap: var(--terrain-space-sm);

  h4 { margin: 0; color: var(--color-navy); font-size: var(--terrain-font-caption); text-transform: uppercase; letter-spacing: .03em; }
}

.terrain-review-queue__group-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  gap: var(--terrain-space-md);
}

.terrain-review-queue__select-all {
  align-items: center;
  color: var(--color-ghost);
  display: flex;
  font-size: var(--terrain-font-caption);
  font-weight: 600;
  gap: var(--terrain-space-xs);
}

.terrain-review-queue__bulk-bar {
  align-items: center;
  background: var(--color-mist);
  border-radius: var(--terrain-radius-md);
  display: flex;
  flex-wrap: wrap;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-sm) var(--terrain-space-md);

  span { color: var(--color-navy); font-weight: 600; font-size: var(--terrain-font-caption); }

  button {
    background: var(--color-navy);
    border: 0;
    border-radius: var(--terrain-radius-xl);
    color: var(--color-white);
    cursor: pointer;
    font-size: var(--terrain-font-caption);
    font-weight: 600;
    padding: var(--terrain-space-xs) var(--terrain-space-md);

    &:hover:not(:disabled) { background: var(--color-steel); }
    &:disabled { background: var(--color-ghost); cursor: not-allowed; }
  }
}

.terrain-review-queue__bulk-clear {
  background: var(--color-white) !important;
  border: 1px solid var(--color-line) !important;
  color: var(--color-graphite) !important;
}

.terrain-review-queue__item {
  align-items: stretch;
  display: flex;
  gap: var(--terrain-space-xs);
}

.terrain-review-queue__item-checkbox {
  align-items: center;
  display: flex;
  padding: 0 var(--terrain-space-xs);
}

.terrain-review-queue__item-button {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  cursor: pointer;
  display: grid;
  gap: var(--terrain-space-xs);
  padding: var(--terrain-space-md);
  text-align: left;
  width: 100%;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
}

.terrain-review-queue__item--active .terrain-review-queue__item-button {
  border-color: var(--color-orange);
  box-shadow: 0 0 0 1px var(--color-orange);
}

.terrain-review-queue__item-date { color: var(--color-ghost); font-size: .8rem; }
.terrain-review-queue__item-respondent { color: var(--color-navy); font-weight: 600; font-size: .85rem; word-break: break-word; }
.terrain-review-queue__detail-id { color: var(--color-ghost); font-size: .8rem; margin: 2px 0 0; word-break: break-all; }

.terrain-review-queue__tag {
  align-self: flex-start;
  background: var(--color-mist);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-graphite);
  display: inline-block;
  font-size: var(--terrain-font-caption);
  font-weight: 700;
  padding: 2px var(--terrain-space-sm);

  &--submitted { background: var(--terrain-color-warning-light); border-color: var(--color-orange); color: var(--color-orange); }
  &--en_revision, &--corregida { background: var(--color-mist); border-color: var(--color-steel); color: var(--color-steel); }
  &--observada { background: var(--terrain-color-warning-light); border-color: var(--color-warning); color: var(--color-warning); }
  &--aprobada { background: var(--terrain-color-success-light); border-color: var(--color-success); color: var(--color-success); }
  &--rechazada { background: var(--terrain-color-danger-light); border-color: var(--color-danger); color: var(--color-danger); }
  &--retirada { background: var(--color-mist); border-color: var(--color-line); color: var(--color-ghost); }
}

.terrain-review-queue__detail {
  background: var(--color-white);
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-lg);
  display: grid;
  gap: var(--terrain-space-lg);
  padding: var(--terrain-space-lg);
}

.terrain-review-queue__detail-header { align-items: center; display: flex; gap: var(--terrain-space-md); }
.terrain-review-queue__detail-header h3 { margin: 0; }

.terrain-review-queue__location { display: grid; gap: var(--terrain-space-sm); }
.terrain-review-queue__location h4 { margin: 0; color: var(--color-navy); font-size: var(--terrain-font-caption); text-transform: uppercase; letter-spacing: .03em; }

.terrain-review-queue__correction {
  background: var(--terrain-color-warning-light);
  border: 1px solid var(--color-warning);
  border-radius: var(--terrain-radius-md);
  display: grid;
  gap: var(--terrain-space-sm);
  padding: var(--terrain-space-md);
}
.terrain-review-queue__correction-actions { display: flex; justify-content: flex-end; gap: var(--terrain-space-sm); }
.terrain-review-queue__correction-save {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}
.terrain-review-queue__correction-discard {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);

  &:hover:not(:disabled) { background: var(--color-mist); }
  &:disabled { cursor: not-allowed; opacity: .6; }
}

.terrain-review-queue__actions { border-top: 1px solid var(--color-line); display: grid; gap: var(--terrain-space-sm); padding-top: var(--terrain-space-md); }
.terrain-review-queue__comment-label { color: var(--color-navy); display: grid; font-weight: 600; gap: var(--terrain-space-xs); }
.terrain-review-queue__hint { color: var(--color-ghost); font-size: .8rem; font-weight: 400; }

.terrain-review-queue__comment {
  border: 1px solid var(--color-line);
  border-radius: var(--terrain-radius-md);
  font: inherit;
  padding: var(--terrain-space-sm);
  resize: vertical;
  transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

  &:hover { border-color: var(--color-steel); }
  &:focus { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); outline: none; }
}

.terrain-review-queue__buttons { display: flex; flex-wrap: wrap; gap: var(--terrain-space-md); }

.terrain-review-queue__buttons button {
  background: var(--color-navy);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-steel); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}

.terrain-review-queue__error {
  background: var(--terrain-color-danger-light);
  border-left: 4px solid var(--color-danger);
  border-radius: var(--terrain-radius-sm);
  color: var(--color-danger);
  margin: 0;
  padding: var(--terrain-space-md);
}

.terrain-review-queue__status, .terrain-review-queue__empty { color: var(--color-ghost); margin: 0; }

.terrain-review-queue__bulk-modal {
  display: grid;
  gap: var(--terrain-space-md);
  padding: var(--terrain-space-lg);

  h2 { margin: 0; color: var(--color-navy); }
  p { margin: 0; color: var(--color-ghost); }
}

.terrain-review-queue__bulk-modal-actions { display: flex; justify-content: flex-end; gap: var(--terrain-space-sm); }

.terrain-review-queue__bulk-modal-cancel {
  background: var(--color-white);
  border: 1px solid var(--color-navy);
  border-radius: var(--terrain-radius-xl);
  color: var(--color-navy);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-mist); }
  &:disabled { cursor: not-allowed; opacity: .6; }
}

.terrain-review-queue__bulk-modal-confirm {
  background: var(--color-danger);
  border: 0;
  border-radius: var(--terrain-radius-xl);
  color: var(--color-white);
  cursor: pointer;
  font-weight: 600;
  padding: var(--terrain-space-sm) var(--terrain-space-lg);
  transition: background-color var(--terrain-transition);

  &:hover:not(:disabled) { background: var(--color-danger-dark, var(--color-danger)); }
  &:disabled { background: var(--color-ghost); cursor: not-allowed; }
}

@media (max-width: 720px) {
  .terrain-review-queue__layout { grid-template-columns: 1fr; }
}
</style>
